import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_BASE}/api` });

// Token management
const TOKEN_KEY = 'today_session_token';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// Add token to all requests
api.interceptors.request.use(config => {
  const token = getStoredToken();
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  return config;
});

export function formatError(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Auth
export const authRegister = (data) => api.post('/auth/register', data);
export const authLogin = (data) => api.post('/auth/login', data);
export const authMe = () => api.get('/auth/me');
export const authLogout = () => api.post('/auth/logout');
export const authSession = (data) => api.post('/auth/session', data);
export const authGoogleDirect = (data) => api.post('/auth/google-direct', data);
export const authGoogleCode = (data) => api.post('/auth/google-code', data);
export const authLinkDevice = (data) => api.post('/auth/link-device', data);

// Users - device based
export const userLogin = (data) => api.post('/users/login', data);
export const getUser = (deviceId, userId) => api.get(`/users/${deviceId}`, { params: { user_id: userId } });
export const updatePreferences = (data) => api.post('/users/preferences', data);
// Post-test contact capture (email required, phone optional)
export const captureContact = (data) => api.post('/users/contact', data);
export const getPreferences = (deviceId) => api.get(`/users/${deviceId}/preferences`);
export const getMilestoneStats = (deviceId) => api.get(`/users/${deviceId}/milestone-stats`);

// Mood
export const getMoodFoods = () => api.get('/mood-foods');
export const getMoodFood = (moodId, deviceId, region) => api.get(`/mood-foods/${moodId}`, { params: { device_id: deviceId, region } });
export const logMood = (data) => api.post('/mood/log', data);
export const getMoodTracker = (deviceId) => api.get('/mood/tracker', { params: { device_id: deviceId } });
export const getMoodInsights = (deviceId) => api.get('/mood/insights', { params: { device_id: deviceId } });
export const getMoodActivities = (moodId, deviceId) => api.get(`/mood/activities/${moodId}`, { params: { device_id: deviceId } });
export const toggleMoodActivity = (data) => api.post('/mood/activities/toggle', data);
export const getTodaysFood = (deviceId) => api.get('/todays-food', { params: { device_id: deviceId } });

// Programs
export const getPrograms = () => api.get('/programs');
export const getProgram = (programId, deviceId) => api.get(`/programs/${programId}`, { params: { device_id: deviceId } });
export const getProgramDay = (programId, day, deviceId) => api.get(`/programs/${programId}/day/${day}`, { params: { device_id: deviceId } });
export const enrollProgram = (programId, data) => api.post(`/programs/${programId}/enroll`, data);
export const getUserEnrollments = (deviceId, userId) => api.get('/user/enrollments', { params: { device_id: deviceId, user_id: userId } });
export const cancelProgram = (programId, data) => api.post(`/programs/${programId}/cancel`, data);
export const updateProgress = (data) => api.post('/programs/progress', data);

// Schedule
export const getTodayPlan = (deviceId, userId) => api.get('/user/today-plan', { params: { device_id: deviceId, user_id: userId } });
export const getWeeklyPlan = (deviceId) => api.get('/user/weekly-plan', { params: { device_id: deviceId } });
export const completeSchedule = (scheduleId, data) => api.post(`/user/schedule/${scheduleId}/complete`, data);
export const ignoreSchedule = (scheduleId, data) => api.post(`/user/schedule/${scheduleId}/ignore`, data);

// Gratitude
export const getGratitudeEntries = (deviceId) => api.get('/gratitude/entries', { params: { device_id: deviceId } });
export const getGratitudeToday = (deviceId) => api.get('/gratitude/today', { params: { device_id: deviceId } });
export const saveGratitude = (data) => api.post('/gratitude/save', data);

// Puzzles
export const getDailyPuzzles = () => api.get('/puzzles/daily');
export const getPuzzleStats = (deviceId) => api.get(`/puzzles/stats/${deviceId}`);
export const completePuzzle = (data) => api.post('/puzzles/complete', data);

// Personality
export const getPersonalityQuestions = (deviceId, occupation) => api.get('/personality/questions', { params: { device_id: deviceId, occupation } });
export const submitPersonality = (data) => api.post('/personality/submit', data);
export const getPersonalityResult = (deviceId) => api.get('/personality/result', { params: { device_id: deviceId } });
export const generatePersonalityReport = (data) => api.post('/personality/generate-report', data);
export const getCachedReport = (deviceId) => api.get('/personality/cached-report', { params: { device_id: deviceId } });

// Motivational Quote
export const getMotivationalQuote = () => api.get('/motivational-quote');

// Premium
export const getPremiumStatus = (deviceId) => api.get('/premium/status', { params: { device_id: deviceId } });
export const getPremiumFeatures = () => api.get('/premium/features');

// Personal Plan
export const getPersonalPlan = (deviceId) => api.get('/user/personal-plan', { params: { device_id: deviceId } });
export const savePersonalPlan = (data) => api.post('/user/personal-plan', data);

// IQ Test (server-backed)
export const getIQQuestions = () => api.get('/personality/iq-questions');
export const submitIQAnswers = (data) => api.post('/personality/iq-submit', data);
export const getIQResult = (deviceId) => api.get('/personality/iq-result', { params: { device_id: deviceId } });

// Mood wellness (server-backed body chemistry + breathing)
export const getMoodWellness = (moodId) => api.get(`/mood/wellness/${moodId}`);

// Meal personalization
export const personalizeMeal = (data) => api.post('/programs/meal/personalize', data);

// Location / currency
export const getUserLocation = () => api.get('/user/location');
export const convertPrice = (amountInr, currency) => api.post('/pricing/convert', { amount_inr: amountInr, currency });

// User entitlements (programs/reports/premium unlocked).
// Pass the current user's email (if authenticated) so purchases made with
// the same email on another device are also returned.
export const getEntitlements = (deviceId, email) =>
  api.get('/user/entitlements', {
    params: email ? { device_id: deviceId, email } : { device_id: deviceId },
  });

export default api;
